import React, { useEffect, useState, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Button,
    Modal,
    Form,
    Spinner,
    Alert,
    Badge,
    InputGroup,
} from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

const EXPENSE_TYPES = [
    "Daily Expense",
    "Utility Expense",
];

const ExpenseTypesPage = () => {
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    // Filters
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);

    // Form State
    const [type, setType] = useState("Daily Expense");
    const [name, setName] = useState("");

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken")
            ? `Bearer ${localStorage.getItem("authToken")}`
            : "",
    });

    // Fetch Expense Types
    const fetchExpenseTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const params = {};
            if (search) params.search = search;
            if (typeFilter) params.type = typeFilter;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/expense-types`,
                {
                    headers: getAuthHeaders(),
                    params,
                }
            );

            setExpenseTypes(response.data?.data || response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load expense types.");
        } finally {
            setLoading(false);
        }
    }, [search, typeFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchExpenseTypes();
        }, 300);

        return () => clearTimeout(timer);
    }, [fetchExpenseTypes]);

    // Handle Open/Close Modal
    const handleOpenModal = (item = null) => {
        setErrors({});
        if (item) {
            setEditItem(item);
            setType(item.type || "Daily Expense");
            setName(item.name || "");
        } else {
            setEditItem(null);
            setType("Daily Expense");
            setName("");
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditItem(null);
        setType("Daily Expense");
        setName("");
        setErrors({});
    };

    // Store / Update
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");
        setError("");

        const payload = { type, name };

        try {
            if (editItem) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/admin/expense-types/${editItem.id}`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage("Expense type updated successfully.");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/admin/expense-types`,
                    payload,
                    { headers: getAuthHeaders() }
                );
                setMessage("Expense type created successfully.");
            }

            handleCloseModal();
            await fetchExpenseTypes();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setSaving(false);
        }
    };

    // Delete
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this expense type?")) return;

        try {
            setError("");
            setMessage("");
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/admin/expense-types/${id}`,
                { headers: getAuthHeaders() }
            );
            setMessage("Expense type deleted successfully.");
            await fetchExpenseTypes();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete expense type.");
        }
    };

    const getTypeBadgeVariant = (type) => {
        switch (type) {
            case "Daily Expense":
                return "info";
            case "Salary Expense":
                return "success";
            case "Utility Expense":
                return "warning";
            default:
                return "secondary";
        }
    };

    return (
        <PageLayout>
            <Container fluid className="py-4">
                {/* Header Section */}
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">Expense Types</h2>
                        <p className="text-muted mb-0">Manage expense categories and classification types.</p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="primary"
                            className="px-4 fw-semibold"
                            onClick={() => handleOpenModal()}
                        >
                            + Add Expense Type
                        </Button>
                    </Col>
                </Row>

                {/* System Messages */}
                {message && (
                    <Alert variant="success" dismissible onClose={() => setMessage("")}>
                        {message}
                    </Alert>
                )}

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {/* Filters Section */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-3">
                            <Col md={7} lg={8}>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search by name..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="bg-light border-0 py-2"
                                    />
                                    {search && (
                                        <Button
                                            variant="outline-secondary"
                                            className="border-0 bg-light"
                                            onClick={() => setSearch("")}
                                        >
                                            ✕
                                        </Button>
                                    )}
                                </InputGroup>
                            </Col>
                            <Col md={5} lg={4}>
                                <Form.Select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="bg-light border-0 py-2"
                                >
                                    <option value="">All Expense Types</option>
                                    {EXPENSE_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table Section */}
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <div className="mt-2 text-muted">Loading expense types...</div>
                            </div>
                        ) : expenseTypes.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                No expense types found. Click "+ Add Expense Type" to create one.
                            </div>
                        ) : (
                            <Table responsive hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "80px" }}>#</th>
                                        <th>Name</th>
                                        <th>Category Type</th>
                                        <th className="text-end pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenseTypes.map((item, index) => (
                                        <tr key={item.id || index}>
                                            <td className="ps-4 fw-bold text-muted">{index + 1}</td>
                                            <td className="fw-semibold text-dark">{item.name}</td>
                                            <td>
                                                <Badge
                                                    bg={getTypeBadgeVariant(item.type)}
                                                    className="px-3 py-2 text-dark bg-opacity-10 fw-semibold border"
                                                >
                                                    {item.type}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-4">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => handleOpenModal(item)}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(item.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>

                {/* ADD / EDIT MODAL */}
                <Modal
                    show={showModal}
                    onHide={handleCloseModal}
                    centered
                    backdrop="static"
                    keyboard={false}
                >
                    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
                        <Modal.Title className="fw-bold fs-4 text-dark">
                            {editItem ? "Edit Expense Type" : "Add Expense Type"}
                        </Modal.Title>
                    </Modal.Header>

                    <Form onSubmit={handleSubmit}>
                        <Modal.Body className="px-4 py-3">
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Type Category <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={type}
                                    onChange={(e) => {
                                        setType(e.target.value);
                                        setErrors((prev) => ({ ...prev, type: "" }));
                                    }}
                                    isInvalid={!!errors.type}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                >
                                    {EXPENSE_TYPES.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.type) ? errors.type[0] : errors.type}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold small text-muted text-uppercase">
                                    Expense Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. Office Stationery, Electricity Bill"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                        setErrors((prev) => ({ ...prev, name: "" }));
                                    }}
                                    isInvalid={!!errors.name}
                                    className="bg-light border-0 py-2 px-3 text-dark"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {Array.isArray(errors.name) ? errors.name[0] : errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Modal.Body>

                        <Modal.Footer className="border-0 px-4 pb-4 pt-2 d-flex justify-content-end gap-2">
                            <Button
                                variant="secondary"
                                className="px-4 py-2 fw-semibold"
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 fw-semibold text-uppercase"
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                    </>
                                ) : editItem ? (
                                    "Update Type"
                                ) : (
                                    "Create Type"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Container>
        </PageLayout>
    );
};

export default ExpenseTypesPage;